// ---------------------------------------------------------------------------
// DnD Email Editor – Color controls & presets (shared)
//
// A compact color control: a [swatch][hex field] row. Clicking the swatch opens
// a small popover (portalled to <body> so the inspector's overflow never clips
// it) offering a native color picker plus brand preset swatches — keeping the
// inspector row itself uncluttered.
//
// Preset sources:
//   • Primary/Standard — the master-account brand Color Palette.
//   • Standard falls back to a fixed neutral/common palette when empty.
// ---------------------------------------------------------------------------

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';

export interface PresetColor {
  value: string;
  label: string;
}

/** Fixed neutral + common colors, shown under the "Standard" heading. */
export const STANDARD_COLOR_PRESETS: PresetColor[] = [
  { value: '#000000', label: 'Black' },
  { value: '#434343', label: 'Dark gray' },
  { value: '#666666', label: 'Gray' },
  { value: '#999999', label: 'Light gray' },
  { value: '#cccccc', label: 'Silver' },
  { value: '#ffffff', label: 'White' },
  { value: '#d93025', label: 'Red' },
  { value: '#e8710a', label: 'Orange' },
  { value: '#f9ab00', label: 'Amber' },
  { value: '#188038', label: 'Green' },
  { value: '#1a73e8', label: 'Blue' },
  { value: '#8e24aa', label: 'Purple' },
];

/**
 * Preset swatch groups provided to the color popover:
 *   primary  — master-account brand colors marked "primary"
 *   standard — master-account brand colors marked "standard"
 * Both come from the master Color Palette; when `standard` is empty the popover
 * falls back to the built-in STANDARD_COLOR_PRESETS.
 */
export interface ColorPresets {
  primary: PresetColor[];
  standard: PresetColor[];
}

export const ColorPresetsContext = createContext<ColorPresets>({
  primary: [],
  standard: [],
});

const normalizeHex = (c: string) => c.trim().toLowerCase();

// ---- Swatch grid (used inside the popover) --------------------------------

function SwatchGrid({
  colors,
  active,
  onPick,
}: {
  colors: PresetColor[];
  active: string;
  onPick: (v: string) => void;
}) {
  return (
    <div className="dnd-color-pop-grid">
      {colors.map((c, i) => {
        const selected = active !== '' && normalizeHex(c.value) === active;
        return (
          <button
            key={`${c.value}-${i}`}
            type="button"
            className={`dnd-swatch${selected ? ' selected' : ''}`}
            style={{ backgroundColor: c.value }}
            title={`${c.label} (${c.value})`}
            aria-label={`${c.label} ${c.value}`}
            aria-pressed={selected}
            onClick={() => onPick(c.value)}
          />
        );
      })}
    </div>
  );
}

// ---- Color popover --------------------------------------------------------

/** Optional "inherit" choice (Global text color / transparent background). */
export interface InheritOption {
  label: string;
  title: string;
  active: boolean;
  onSelect: () => void;
}

function ColorPopover({
  anchorEl,
  value,
  onChange,
  onClose,
  inherit,
}: {
  anchorEl: HTMLElement | null;
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
  inherit?: InheritOption;
}) {
  const { primary, standard } = useContext(ColorPresetsContext);
  const standardRow = standard.length > 0 ? standard : STANDARD_COLOR_PRESETS;
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Position under the anchor swatch, clamped to the viewport (flips above when
  // there isn't room below).
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const width = 240;
    const height = panelRef.current?.offsetHeight ?? 280;
    let left = Math.min(r.left, window.innerWidth - width - 8);
    left = Math.max(8, left);
    let top = r.bottom + 6;
    if (top + height > window.innerHeight - 8) {
      top = Math.max(8, r.top - height - 6);
    }
    setPos({ top, left });
  }, [anchorEl, primary.length, standardRow.length, !!inherit]);

  // Close on outside pointer-down or Esc.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorEl?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorEl, onClose]);

  const active = normalizeHex(value || '');

  return createPortal(
    <div
      ref={panelRef}
      className="dnd-color-pop"
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label="Choose a color"
    >
      {inherit && (
        <button
          type="button"
          className={`dnd-color-pop-inherit${inherit.active ? ' active' : ''}`}
          title={inherit.title}
          onClick={() => { inherit.onSelect(); onClose(); }}
        >
          {inherit.label}
        </button>
      )}

      <div className="dnd-color-pop-custom">
        <input
          type="color"
          value={value || '#1a73e8'}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Custom color"
        />
        <input
          type="text"
          value={value}
          placeholder="#000000"
          onChange={(e) => onChange(e.target.value)}
          aria-label="Hex color value"
        />
      </div>

      {primary.length > 0 && (
        <>
          <div className="dnd-color-pop-label">Primary</div>
          <SwatchGrid colors={primary} active={active} onPick={(c) => { onChange(c); onClose(); }} />
        </>
      )}
      <div className="dnd-color-pop-label">Standard</div>
      <SwatchGrid colors={standardRow} active={active} onPick={(c) => { onChange(c); onClose(); }} />
    </div>,
    document.body,
  );
}

// ---- Compact trigger ([swatch][hex]) --------------------------------------

/**
 * The inspector-facing control: a color swatch button + a hex text field. The
 * swatch opens the popover; the field stays inline for quick hex entry.
 */
export function ColorTrigger({
  value,
  onChange,
  swatchStyle,
  swatchClass,
  placeholder,
  inherit,
}: {
  value: string;
  onChange: (v: string) => void;
  swatchStyle?: CSSProperties;
  swatchClass?: string;
  placeholder?: string;
  inherit?: InheritOption;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="dnd-color-input">
      <button
        ref={btnRef}
        type="button"
        className={`dnd-color-swatch-btn${swatchClass ? ` ${swatchClass}` : ''}`}
        style={swatchStyle}
        onClick={() => setOpen((o) => !o)}
        aria-label="Choose color"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Choose color"
      />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {open && (
        <ColorPopover
          anchorEl={btnRef.current}
          value={value}
          onChange={onChange}
          onClose={() => setOpen(false)}
          inherit={inherit}
        />
      )}
    </div>
  );
}

/**
 * Plain color control (no inherit concept): a swatch + hex field, presets in the
 * popover. Shared by the inspector and the Design panel.
 */
export function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <ColorTrigger
        value={value}
        onChange={onChange}
        swatchStyle={{ backgroundColor: value || '#ffffff' }}
        placeholder="#000000"
      />
    </div>
  );
}
